import { CSSProperties, memo, MouseEvent, ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import type { DataTableRow } from '../dataTableFeatures';
import { samePropsByRowData } from './samePropsByRowData';
import { virtualRowStyle } from './virtualRowStyle';

/** Связь строки с dnd-kit. Объект меняется вместе с атрибутами (признак «несут»): при стабильных сенсорах
 *  `setNodeRef` и обработчики `useSortable` не меняются, поэтому строка и её ручка переноса пропускают рендер корня. */
export interface ISortableRowHandle {
    setNodeRef: (element: HTMLElement | null) => void;
    attributes: DraggableAttributes;
    listeners: DraggableSyntheticListeners;
}

/** Отрисовка и события строки данных. Живут в корне таблицы: строка читает их из ref при своём рендере и в момент
 *  события, поэтому события `li` и строки идут в свежий корень. Элементы, нарисованные `renderLine` (чекбокс,
 *  закрепление, `rowActions`, кнопка разворота), у пропущенной строки остаются со своего рендера. */
export interface IDataTableBodyRowRenderer<T extends RowData> {
    renderLine: (props: IDataTableBodyRowContentProps<T>) => ReactNode;
    renderBelow: (props: IDataTableBodyRowContentProps<T>) => ReactNode;
    onRowElement: (row: DataTableRow<T>, element: HTMLElement) => void;
    onLineClick: (row: DataTableRow<T>, e: MouseEvent) => void;
    onLineMouseDown: (e: MouseEvent) => void;
    onLineDoubleClick: (row: DataTableRow<T>, e: MouseEvent) => void;
    onLineContextMenu: (row: DataTableRow<T>, e: MouseEvent) => void;
}

/** Пропсы содержимого строки (`.dtb__rowline` и то, что под ней): от них зависит, перерисуются ли её ячейки. */
export interface IDataTableBodyRowContentProps<T extends RowData> {
    /** Сравнивается по `row.original`: новый объект Row с теми же данными строку не перерисовывает. */
    row: DataTableRow<T>;
    /** Есть у строки, которую можно перетащить. */
    sortable?: ISortableRowHandle;
    selected: boolean;
    someSelected: boolean;
    selectionDisabled: boolean;
    pinned: boolean;
    expanded: boolean;
    canExpand: boolean;
    loading: boolean;
    editingColId?: string;
    editableKey?: string;
    /** Версия вида всех строк: меняется вместе с колонками, раскладкой и render-пропсами таблицы. */
    view: object;
    /** Версия вида этой строки (`rowVersion` таблицы). */
    version?: string | number;
    renderer: { readonly current: IDataTableBodyRowRenderer<T> };
}

/** Пропсы строки: оболочка `li` (позиция, классы, ARIA, перенос) плюс содержимое. */
export interface IDataTableBodyRowProps<T extends RowData> extends IDataTableBodyRowContentProps<T> {
    rowIndex: number;
    virtualIndex?: number;
    /** Позиция в виртуальном списке; без неё строка стоит в потоке. */
    top?: number;
    measure?: (el: HTMLElement | null) => void;
    /** Сдвиг строки по вертикали, пока над списком несут другую; у несомой — 0; без сдвига dnd-kit — undefined. */
    dndY?: number;
    dndTransition?: string;
    className: string;
    ariaSelected?: boolean;
    ariaLevel?: number;
}

function rowStyle({
    top,
    sortable,
    dndY,
    dndTransition,
}: Pick<IDataTableBodyRowProps<RowData>, 'top' | 'sortable' | 'dndY' | 'dndTransition'>): CSSProperties | undefined {
    // сдвиг dnd-kit (только по вертикали) складывается с офсетом виртуализации;
    // активная строка не двигается (dndY у неё 0) — за курсором едет DragOverlay
    // (transform in-flow элемента растил scroll-область до бесконечного автоскролла)
    if (top !== undefined) {
        return {
            ...virtualRowStyle(top),
            // при перетаскивании строка едет transform-ом поверх своей позиции
            ...(dndY ? { transform: `translate3d(0, ${dndY}px, 0)` } : null),
            transition: dndTransition,
        };
    }
    if (dndY !== undefined) return { transform: `translate3d(0, ${dndY}px, 0)`, transition: dndTransition };
    return sortable ? { transition: dndTransition } : undefined;
}

/** Содержимое строки: служебные треки, ячейки, действия и то, что под строкой. Отдельный memo от оболочки `li`:
 *  сдвиг строки при прокрутке, смена текущей строки и её номера ячейки не перерисовывают. */
function DataTableBodyRowContentImpl<T extends RowData>(props: IDataTableBodyRowContentProps<T>) {
    const { row, renderer } = props;
    return (
        <>
            {/* клик по строке — мышиная доп.возможность; доступные контролы — чекбокс/кнопки */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div
                className="dtb__rowline"
                data-row={row.id}
                onClick={(e) => renderer.current.onLineClick(row, e)}
                onMouseDown={(e) => renderer.current.onLineMouseDown(e)}
                onDoubleClick={(e) => renderer.current.onLineDoubleClick(row, e)}
                onContextMenu={(e) => renderer.current.onLineContextMenu(row, e)}
            >
                {renderer.current.renderLine(props)}
            </div>
            {renderer.current.renderBelow(props)}
        </>
    );
}

const DataTableBodyRowContent = memo(
    DataTableBodyRowContentImpl,
    samePropsByRowData,
) as typeof DataTableBodyRowContentImpl;

/** Строка ещё и по индексу в данных: его получает скрипт строки (`onRowRendered`), а сортировка держит строку на месте,
 *  когда вставка выше сдвигает индекс. Ячейкам индекс не нужен — их сравнение без него. */
const sameRowProps = <P extends { row: { id: string; original: unknown; index: number } }>(prev: P, next: P) =>
    prev.row.index === next.row.index && samePropsByRowData(prev, next);

/** Строка данных таблицы: `li` с позицией, классами и событиями; содержимое рисует корень через `renderer`. */
function DataTableBodyRow<T extends RowData>(props: IDataTableBodyRowProps<T>) {
    // позиция в списке: меняет только стиль и data-index строки
    const { top, virtualIndex, measure, dndY, dndTransition, ...rendered } = props;
    const { rowIndex, className, ariaSelected, ariaLevel, ...content } = rendered;
    const { row, sortable, canExpand, expanded, renderer } = content;
    const elementRef = useRef<HTMLLIElement | null>(null);
    // Стабильный ref: инлайновый звался на каждом рендере дважды (null, элемент) — measureElement(null) обходил весь
    // кэш элементов окна, а повторный замер строки — синхронный пересчёт раскладки.
    const attach = useCallback(
        (el: HTMLLIElement | null) => {
            elementRef.current = el;
            measure?.(el);
            sortable?.setNodeRef(el);
        },
        [measure, sortable],
    );
    // Скрипт строки (onRowRendered) — после рендера, который мог тронуть её DOM или данные; сдвиг строки по списку
    // (позиционные пропсы) скрипт не повторяет.
    const renderedRef = useRef<typeof rendered | null>(null);
    useLayoutEffect(() => {
        const prev = renderedRef.current;
        renderedRef.current = rendered;
        if (prev && sameRowProps(prev, rendered)) return;
        if (elementRef.current) renderer.current.onRowElement(row, elementRef.current);
    });
    return (
        <li
            data-index={virtualIndex}
            role="row"
            data-row-key={row.id}
            aria-rowindex={rowIndex >= 0 ? rowIndex + 1 : undefined}
            aria-selected={ariaSelected}
            aria-level={ariaLevel}
            aria-expanded={canExpand ? expanded : undefined}
            ref={attach}
            className={className}
            style={rowStyle({ top, sortable, dndY, dndTransition })}
            tabIndex={-1}
        >
            <DataTableBodyRowContent<T> {...content} />
        </li>
    );
}

export default memo(DataTableBodyRow, sameRowProps) as typeof DataTableBodyRow;
